﻿/**
 * useProfileCrud — CRUD hooks for user profiles + avatar upload
 */
import { useCallback } from 'react';
import { supabase } from '@/core/supabase';
import { useSupabaseQuery } from '@/shared/hooks/useSupabaseQuery';
import { useAuthStore } from '@/shared/stores/authStore';
import type { ProfileRow, ProfileUpdate } from '@/core/supabase/database.types';

// ===== Fetch current user's profile =====
export function useMyProfile() {
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;

  const queryFn = useCallback(() => {
    return supabase
      .from('profiles')
      .select('*')
      .eq('id', userId ?? '')
      .maybeSingle();
  }, [userId]);

  return useSupabaseQuery<ProfileRow | null>(queryFn, [userId], {
    enabled: !!userId,
  });
}

// ===== Fetch any user's public profile =====
export function useUserProfile(userId: string | null) {
  const queryFn = useCallback(() => {
    return supabase
      .from('profiles')
      .select('*')
      .eq('id', userId ?? '')
      .maybeSingle();
  }, [userId]);

  return useSupabaseQuery<ProfileRow | null>(queryFn, [userId], {
    enabled: !!userId,
  });
}

// ===== Fetch user's published novels =====
export function useUserNovels(authorId: string | null) {
  const queryFn = useCallback(() => {
    return supabase
      .from('novels')
      .select('*')
      .eq('author_id', authorId ?? '')
      .in('status', ['ongoing', 'completed'])
      .order('updated_at', { ascending: false })
      .limit(100);
  }, [authorId]);

  return useSupabaseQuery(queryFn, [authorId], {
    enabled: !!authorId,
  });
}

// ===== Update profile =====
export async function updateProfile(
  userId: string,
  updates: ProfileUpdate,
): Promise<{ data: ProfileRow | null; error: string | null }> {
  // Client-side validation
  if (updates.username !== undefined) {
    const u = updates.username?.trim();
    if (!u || u.length < 2) return { data: null, error: '昵称至少需要 2 个字符' };
    if (u.length > 30) return { data: null, error: '昵称不能超过 30 个字符' };
  }
  if (updates.bio !== undefined && (updates.bio?.length ?? 0) > 500) {
    return { data: null, error: '简介不能超过 500 个字符' };
  }
  if (updates.signature !== undefined && (updates.signature?.length ?? 0) > 100) {
    return { data: null, error: '个性签名不能超过 100 个字符' };
  }
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .maybeSingle();
  return { data, error: error?.message ?? null };
}

// ===== Avatar upload =====
export async function uploadAvatar(
  userId: string,
  file: File,
): Promise<{ url: string | null; error: string | null }> {
  // Validate file
  if (file.size > 2 * 1024 * 1024) {
    return { url: null, error: '图片大小不能超过 2MB' };
  }
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  if (!allowedTypes.includes(file.type)) {
    return { url: null, error: '仅支持 JPG/PNG/WebP/GIF 格式' };
  }

  const mimeToExt: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
  };
  const ext = mimeToExt[file.type] ?? 'jpg';
  const filePath = `${userId}/avatar.${ext}`;

  // Upload (upsert to overwrite existing)
  const { error: uploadErr } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, { upsert: true, contentType: file.type });
  if (uploadErr) {
    return { url: null, error: uploadErr.message };
  }

  // Get public URL
  const { data: urlData } = supabase.storage
    .from('avatars')
    .getPublicUrl(filePath);

  // Store stable URL in DB (without cache-bust param)
  const stableUrl = urlData.publicUrl;
  // Bust cache with timestamp for immediate UI refresh only
  const displayUrl = stableUrl + '?t=' + Date.now();

  // Update profile with stable URL
  const { error: profileErr } = await supabase
    .from('profiles')
    .update({ avatar_url: stableUrl })
    .eq('id', userId);
  if (profileErr) {
    return { url: displayUrl, error: 'Avatar uploaded but profile update failed: ' + profileErr.message };
  }

  return { url: displayUrl, error: null };
}
